import * as React from "react"
import {
  SourceControlProvider,
  SourceControl,
  PublicEnterpriseVcsConfig,
} from "@modules/graphql/types"
import { ThemeCss, Text } from "gatsby-interface"
import { importSite as importSiteText } from "@modules/locales/default.js"
import { interpolateMessage } from "@modules/locales"
import { useTracker } from "@modules/analytics"
import {
  SourceControlProviderIcon,
  sourceControlProviderLabels,
  useSupportedSourceControlProviders,
} from "@modules/sourceControlProvider"
import { useFlags } from "@modules/featureFlags"
import { MdArrowForward } from "react-icons/md"
import { isSelfHosted } from "../../shared/utils"

const providerButtonCss: ThemeCss = theme => ({
  background: theme.colors.white,
  border: `none`,
  borderRadius: theme.radii[3],
  boxShadow: theme.shadows.raised,
  cursor: `pointer`,
  fontSize: theme.fontSizes[2],
  lineHeight: theme.lineHeights.dense,
  display: `inline-flex`,
  alignItems: `center`,
  justifyContent: `flex-end`,
  "&:hover, &:focus": {
    textDecoration: `underline`,
  },
})

const onPremButtonCss: ThemeCss = theme => [
  providerButtonCss(theme),
  {
    alignItems: `center`,
    color: theme.colors.grey[70],
    justifyContent: `start`,
    marginLeft: `auto`,
    marginRight: `auto`,
    marginBottom: theme.space[3],
    padding: `${theme.space[4]} ${theme.space[7]}`,
    width: `100%`,
  },
]

const defaultProviderButtonCss: ThemeCss = theme => [
  providerButtonCss(theme),
  {
    alignItems: `center`,
    color: theme.colors.black,
    flexDirection: `column`,
    justifyContent: `flex-end`,
    paddingTop: theme.space[7],
    paddingBottom: theme.space[7],
    paddingLeft: 0,
    paddingRight: 0,
    width: 115,
  },
]

const providerIconCss: ThemeCss = theme => ({
  marginBottom: theme.space[4],
  color: `initial`,
  fontSize: theme.fontSizes[10],
})

export type SourceProviderSelectorProps = {
  availableSourceProviders?: SourceControl[]
  onPremisesGitConnections?: PublicEnterpriseVcsConfig[]
  onSelect: (provider: SourceControlProvider, vcsConfigId?: string) => void
  onAddProvider: (provider: SourceControlProvider, vcsConfigId?: string) => void
}

export function SourceProviderSelector({
  availableSourceProviders,
  onPremisesGitConnections = [],
  onSelect,
  onAddProvider,
}: SourceProviderSelectorProps) {
  const availableProviders = useSupportedSourceControlProviders()
  const { trackButtonClicked } = useTracker()
  const { flags } = useFlags()

  const handleSelectSourceProvider = (
    sourceProvider: SourceControlProvider,
    vcsConfigId?: string
  ) => {
    const isConnected = availableSourceProviders?.some(item => {
      const matchesSelectedProvider =
        item.source === sourceProvider &&
        (item.vcsConfigId ? item.vcsConfigId === vcsConfigId : true)
      return matchesSelectedProvider && item.connected
    })

    if (isConnected) {
      trackButtonClicked(`Select sourceProvider`, {
        valueString: sourceProvider,
      })
      onSelect(sourceProvider, vcsConfigId)
    } else {
      trackButtonClicked(`Connect sourceProvider`, {
        valueString: sourceProvider,
      })
      onAddProvider(sourceProvider, vcsConfigId)
    }
  }

  return (
    <div>
      <div
        css={theme => ({
          display: `flex`,
          justifyContent: `center`,
          marginBottom: theme.space[7],
          gap: theme.space[5],
          flexWrap: `wrap`,
        })}
      >
        {availableProviders.map(sourceProvider => {
          return (
            !isSelfHosted(sourceProvider) && (
              <button
                key={sourceProvider}
                css={defaultProviderButtonCss}
                onClick={() => handleSelectSourceProvider(sourceProvider)}
                aria-label={interpolateMessage<"sourceProvider">(
                  importSiteText.actions.chooseSourceProvider,
                  {
                    sourceProvider: sourceControlProviderLabels[sourceProvider],
                  }
                )}
              >
                <SourceControlProviderIcon
                  sourceControlProvider={sourceProvider}
                  css={providerIconCss}
                  aria-hidden
                />
                {sourceControlProviderLabels[sourceProvider]}
              </button>
            )
          )
        })}
      </div>
      {flags.onPremVcsConnection && onPremisesGitConnections.length !== 0 ? (
        <div
          css={{
            display: `flex`,
            flexDirection: `column`,
            alignItems: `center`,
          }}
        >
          <Text
            size="S"
            noMarginBottom
            css={theme => ({
              alignSelf: `start`,
              marginBottom: theme.space[3],
            })}
          >
            Self-Hosted
          </Text>
          {onPremisesGitConnections.map(({ id: vcsConfigId, source, name }) => {
            return (
              <button
                key={vcsConfigId}
                css={onPremButtonCss}
                onClick={() => handleSelectSourceProvider(source, vcsConfigId)}
                aria-label={interpolateMessage<"sourceProvider">(
                  importSiteText.actions.chooseSourceProvider,
                  {
                    sourceProvider: name,
                  }
                )}
              >
                <SourceControlProviderIcon
                  sourceControlProvider={source}
                  aria-hidden
                  css={theme => ({ marginRight: theme.space[4] })}
                />
                {name}
                <div
                  css={theme => ({
                    marginLeft: `auto`,
                    display: `none`,

                    [theme.mediaQueries.phablet]: {
                      display: `flex`,
                      alignItems: `center`,
                    },
                  })}
                >
                  <Text as="div" tone="BRAND" noMarginBottom>
                    Select
                  </Text>
                  <MdArrowForward
                    css={theme => ({
                      color: theme.colors.gatsby,
                      fontSize: theme.fontSizes[2],
                      marginLeft: theme.space[1],
                    })}
                  />
                </div>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
